/**
 * PROTOTYPE CONTENT — NOT VERIFIED.
 *
 * Restored verbatim from `design-reference/`. The service line, scope lists,
 * credentials and figures were written to demonstrate the layout. They are not
 * confirmed capabilities, and `company/POSITIONING.md` — which decides what
 * Samloryx Engineering sells at all — is still open. See CONTENT-STATUS.md.
 */
export interface Service {
  no: string
  slug: string
  title: string
  blurb: string
  /** Photograph on the service detail page. */
  image: string
}

export const services: Service[] = [
  {
    no: 'S/01',
    slug: 'civil-roads',
    image: '/images/service-civil-roads.jpg',
    title: 'Civil Engineering & Roads',
    blurb: 'National routes, provincial upgrades, interchanges, bridges and stormwater.',
  },
  {
    no: 'S/02',
    slug: 'building-structures',
    image: '/images/service-building-structures.jpg',
    title: 'Building & Structures',
    blurb: 'Commercial, residential and industrial structures from foundation to handover.',
  },
  {
    no: 'S/03',
    slug: 'mining-infrastructure',
    image: '/images/service-mining-infrastructure.jpg',
    title: 'Mining Infrastructure',
    blurb: 'Haul roads, boxcuts, tailings facilities, conveyors and plant civils.',
  },
  {
    no: 'S/04',
    slug: 'water-sanitation',
    image: '/images/service-water-sanitation.jpg',
    title: 'Water & Sanitation',
    blurb: 'Bulk pipelines, reservoirs, pump stations and wastewater treatment works.',
  },
  {
    no: 'S/05',
    slug: 'energy-electrical',
    image: '/images/service-energy-electrical.jpg',
    title: 'Energy & Electrical',
    blurb: 'Substation civils, transmission foundations, solar BOP and grid connections.',
  },
  {
    no: 'S/06',
    slug: 'project-management',
    image: '/images/service-project-management.jpg',
    title: 'Project & Construction Management',
    blurb: 'Principal-agent, cost control and turnkey delivery under NEC and JBCC.',
  },
]

export interface ServiceDetail {
  lead: string
  scope: string[]
  creds: string[]
  /** `[value, label]` pairs. */
  stat: [string, string][]
}

export const serviceDetail: Record<string, ServiceDetail> = {
  'civil-roads': {
    lead: 'Roads and earthworks are where the company started and where most of our current order book sits — municipal roads, industrial access, terraces and the drainage that keeps them alive.',
    scope: [
      'Bulk earthworks and cut-to-fill',
      'Layerworks, base and surfacing',
      'Concrete culverts and drainage structures',
      'Internal roads, kerbing and lay-bys',
      'Stormwater and attenuation',
      'Traffic accommodation on live routes',
    ],
    creds: ['CIDB 6CE PE', 'Directors: 19 years on national roads', 'Owned plant: graders, rollers, TLBs, tippers'],
    stat: [
      ['96 000 m³', 'moved to date'],
      ['3', 'live earthworks contracts'],
      ['R13 m', 'current contract ceiling'],
    ],
  },
  'building-structures': {
    lead: 'Structural and sitework packages for light industrial, warehousing and residential schemes — we take the concrete and the ground, and we finish what we start.',
    scope: [
      'Platform preparation and foundations',
      'Reinforced concrete slabs and plinths',
      'Hardstanding and super-flat floors',
      'Boundary works, gatehouses and yards',
      'Internal bulk services',
      'Snagging and beneficial occupation',
    ],
    creds: ['CIDB 6GB PE', 'Master Builders SA — application lodged', 'In-house formwork and steelfixing crews'],
    stat: [
      ['18 000 m²', 'hardstanding poured'],
      ['2', 'sitework contracts'],
      ['3 100 m³', 'concrete placed'],
    ],
  },
  'mining-infrastructure': {
    lead: 'Contract civils on live operations in Mpumalanga and North West — access roads, terraces and sumps done without interrupting production.',
    scope: [
      'Access and haul road rehabilitation',
      'Plant terraces and concrete plinths',
      'Pollution control sumps and dams',
      'Cable and pipe trenching',
      'Drainage and dust suppression',
      'Rehabilitation earthworks',
    ],
    creds: ['MHSA-compliant systems in place', 'Red Ticket accredited supervision', 'Mine-standard fleet with proximity detection'],
    stat: [
      ['2', 'mine contracts awarded'],
      ['6.2 km', 'access road under contract'],
      ['40 t', 'design axle loading'],
    ],
  },
  'water-sanitation': {
    lead: 'Bulk pipelines, chambers and reservoir connections for local municipalities. The least glamorous and most consequential work we do.',
    scope: [
      'Bulk pipelines up to 500 mm',
      'Valve and meter chambers',
      'Pressure-reducing stations',
      'Reservoir connections and tie-ins',
      'Reticulation and household connections',
      'Pressure testing and commissioning',
    ],
    creds: ['CIDB 6CE PE', 'Midvaal Local Municipality — appointed 2026', 'Registered on three municipal supplier databases'],
    stat: [
      ['3.4 km', 'pipeline under contract'],
      ['5 400', 'households served'],
      ['400 mm', 'largest diameter to date'],
    ],
  },
  'energy-electrical': {
    lead: 'Civil and trenching work for substations, solar arrays and industrial reticulation — a capability we are actively tendering into as the grid rebuild opens up.',
    scope: [
      'Substation platform civils',
      'Transformer plinths and bunds',
      'Cable trenching and sleeving',
      'Solar PV mounting and piling',
      'Standby generation platforms',
      'As-built documentation',
    ],
    creds: ['ECA(SA) registered electrical partner', 'Directors: substation civils experience', 'Currently tendering — no award yet'],
    stat: [
      ['4', 'tenders submitted'],
      ['2027', 'target first award'],
      ['1', 'IPP framework in progress'],
    ],
  },
  'project-management': {
    lead: 'Where a client wants one accountable party on a small or mid-sized contract, we take the principal-contractor role and carry the programme, cost and risk.',
    scope: [
      'Concept budgeting and feasibility',
      'Procurement and subcontract packaging',
      'JBCC and GCC contract administration',
      'Programme and cost reporting',
      'Quality and materials control',
      'Close-out, as-builts and defect liability',
    ],
    creds: ['SACPCMP registered PrCM on staff', 'CCS Candy estimating', 'ECSA registered professional engineer'],
    stat: [
      ['6', 'contracts under administration'],
      ['R48 m', 'order book managed'],
      ['100%', 'on programme to date'],
    ],
  },
}
